import React, { useState } from "react";
import PropTypes from "prop-types";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha, useTheme } from "@mui/material/styles";

/**
 * Collapsible stats module — closed by default, lazy-mounts children on first
 * expand so heavy tables/maps don't load until opened.
 */
export default function StatsAccordionSection({
  id,
  title,
  subtitle,
  icon,
  badge,
  defaultExpanded = false,
  lazy = true,
  children,
}) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [mounted, setMounted] = useState(defaultExpanded);

  const handleChange = (_event, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded) setMounted(true);
  };

  return (
    <Accordion
      id={id}
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: "16px !important",
        border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
        overflow: "hidden",
        bgcolor: "background.paper",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        "&:before": { display: "none" },
        "&.Mui-expanded": {
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 8px 32px ${alpha(theme.palette.common.black, 0.35)}`
              : `0 8px 28px ${alpha(theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.25,
          minHeight: 64,
          "& .MuiAccordionSummary-content": {
            my: 1,
            alignItems: "center",
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ width: "100%", pr: 1 }}
        >
          {icon ? (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={700}>
                {title}
              </Typography>
              {badge ? (
                <Chip label={badge} size="small" variant="outlined" color="primary" />
              ) : null}
            </Stack>
            {subtitle ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 2, sm: 2.5 }, pt: 0, pb: 2.5 }}>
        {lazy && !mounted ? null : children}
      </AccordionDetails>
    </Accordion>
  );
}

StatsAccordionSection.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.node,
  badge: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  lazy: PropTypes.bool,
  children: PropTypes.node,
};
